/**
 * Geolocation Slice
 *
 * Pure reducers for geolocation state transitions.
 * All side effects (navigator calls, storage, timers) are handled in middleware.
 *
 * Key design:
 * - Discriminated union state enforces invariants
 * - Authority (trust level) is separate from status (operational mode)
 */

import {
  GeoError,
  geoInitialState,
  GeoPermission,
  GeoState,
  UserPosition,
  WatchPolicy,
} from "@/types/geolocation"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export const geolocationSlice = createSlice({
  name: "geolocation",
  initialState: geoInitialState,
  reducers: {
    /**
     * Restore state from sessionStorage on init
     */
    storageRestored(
      state,
      action: PayloadAction<{ position: UserPosition | null }>
    ) {
      const { position } = action.payload
      if (position) {
        state.status = "restored"
        state.authority = "restored"
        state.position = position
      }
    },

    /**
     * Permission state changed (from permissions API or inferred from errors)
     */
    permissionUpdated(state, action: PayloadAction<GeoPermission>) {
      state.permission = action.payload
    },

    /**
     * Watch started, transitioning to acquiring state
     */
    watchStarted(
      state,
      action: PayloadAction<{
        watchId: number
        startedAt: number
        policy: WatchPolicy
      }>
    ) {
      const priorPosition = state.position

      // Map authority for acquiring state (acquiring can't be "live")
      // If we had live, treat as paused since we know provenance
      const acquiringAuthority: "none" | "restored" | "paused" = !priorPosition
        ? "none"
        : state.authority === "live"
          ? "paused"
          : state.authority === "none"
            ? "none"
            : state.authority // "restored" | "paused"

      // Transition to acquiring
      const newState: GeoState = {
        status: "acquiring",
        permission: state.permission,
        authority: acquiringAuthority,
        position: priorPosition,
        error: null,
        watchId: action.payload.watchId,
        watchPolicy: action.payload.policy,
        pausedAt: null,
        startedAt: action.payload.startedAt,
      }

      return newState as GeoState
    },

    /**
     * Received a position fix - transition acquiring → watching, or update position
     */
    positionUpdated(state, action: PayloadAction<UserPosition>) {
      if (
        (state.status === "acquiring" || state.status === "watching") &&
        state.watchId !== null &&
        state.watchPolicy !== null
      ) {
        // Transition to watching with live authority
        const newState: GeoState = {
          status: "watching",
          permission: "granted", // confirmed on successful fix
          authority: "live",
          position: action.payload,
          error: null,
          watchId: state.watchId,
          watchPolicy: state.watchPolicy,
          pausedAt: null,
          startedAt: null, // clear timeout tracker
        }

        return newState as GeoState
      }

      // If somehow called in other states, just return current state
      return state
    },

    /**
     * Pause watching (tab hidden) - transition to paused state
     */
    watchPaused(state, action: PayloadAction<number>) {
      if (
        (state.status === "watching" || state.status === "acquiring") &&
        state.position &&
        state.watchPolicy !== null
      ) {
        const newState: GeoState = {
          status: "paused",
          permission: state.permission,
          authority: "paused",
          position: state.position,
          error: null,
          watchId: null,
          watchPolicy: state.watchPolicy,
          pausedAt: action.payload,
          startedAt: null,
        }

        return newState as GeoState
      }

      return state
    },

    /**
     * Paused too long - degrade to restored
     */
    pausedDegraded(state) {
      if (state.status === "paused") {
        const newState: GeoState = {
          status: "restored",
          permission: state.permission,
          authority: "restored",
          position: state.position,
          error: null,
          watchId: null,
          watchPolicy: null,
          pausedAt: null,
          startedAt: null,
        }

        return newState as GeoState
      }

      return state
    },

    /**
     * Restored position expired - degrade to idle
     */
    restoredExpired(state) {
      if (state.status === "restored") {
        const newState: GeoState = {
          status: "idle",
          permission: state.permission,
          authority: "none",
          position: null,
          error: null,
          watchId: null,
          watchPolicy: null,
          pausedAt: null,
          startedAt: null,
        }

        return newState as GeoState
      }

      return state
    },

    /**
     * Watch error occurred
     * Note: middleware must clearWatch BEFORE dispatching this
     */
    watchErrored(state, action: PayloadAction<GeoError>) {
      const newState: GeoState = {
        status: "error",
        permission:
          action.payload.code === "PERMISSION_DENIED"
            ? "denied"
            : state.permission,
        authority: state.position ? state.authority : "none",
        position: state.position,
        error: action.payload,
        watchId: null,
        watchPolicy:
          action.payload.code === "PERMISSION_DENIED"
            ? null
            : state.watchPolicy,
        pausedAt: null,
        startedAt: null,
      }

      return newState as GeoState
    },

    /**
     * User explicitly stopped watching
     */
    watchStopped(state) {
      const newState: GeoState = {
        status: "idle",
        permission: state.permission,
        authority: "none",
        position: null,
        error: null,
        watchId: null,
        watchPolicy: null,
        pausedAt: null,
        startedAt: null,
      }

      return newState as GeoState
    },

    /**
     * Full reset (logout)
     */
    fullReset() {
      return geoInitialState
    },

    /**
     * Clear error and return to previous state
     */
    errorCleared(state) {
      if (state.status !== "error") return state

      if (state.position) {
        const newState: GeoState = {
          status: "restored",
          permission: state.permission,
          authority: "restored",
          position: state.position,
          error: null,
          watchId: null,
          watchPolicy: null,
          pausedAt: null,
          startedAt: null,
        }

        return newState as GeoState
      } else {
        const newState: GeoState = {
          status: "idle",
          permission: state.permission,
          authority: "none",
          position: null,
          error: null,
          watchId: null,
          watchPolicy: null,
          pausedAt: null,
          startedAt: null,
        }

        return newState as GeoState
      }
    },
  },
})

export const {
  storageRestored,
  permissionUpdated,
  watchStarted,
  positionUpdated,
  watchPaused,
  pausedDegraded,
  restoredExpired,
  watchErrored,
  watchStopped,
  fullReset,
  errorCleared,
} = geolocationSlice.actions

export default geolocationSlice.reducer
